import { menuSlotRecipe } from './recipes/Menu.recipe';
import { selectRecipe } from './recipes/Select.recipe';
import { animationStyles, keyframes } from './animations';
import { defineConfig, defaultConfig, defaultBaseConfig, createSystem } from '@chakra-ui/react';
import { headerRecipe } from './recipes/Header.recipe';
import { inputRecipe } from './recipes/Input.recipe';
import { buttonRecipe } from './recipes/Button.recipe';
import { headingRecipe } from './recipes/Heading.recipe';
import { textRecipe } from './recipes/Text.recipe';
import { panelRecipe } from './recipes/Panel.recipe';
import { fieldRecipe } from './recipes/Field.recipe';
import { fieldsetRecipe } from './recipes/Fieldset.recipe';
import { radioGroupRecipe } from './recipes/RadioGroup.recipe';
import { numberInputRecipe } from './recipes/NumberInput.recipe';
import { editableRecipe } from './recipes/Editable.recipe';
import { badgeRecipe } from './recipes/Badge.recipe';
import { drawerRecipe } from './recipes/Drawer.recipe';
import { switchRecipe } from './recipes/Switch.recipe';
import { dialogSlotRecipe } from './recipes/Dialog.recipe';
import { closeButtonRecipe } from './recipes/CloseButton';
import { textareaRecipe } from './recipes/Textarea.recipe';
import { comboboxRecipe } from './recipes/Combobox.recipe';
import { emptyStateRecipe } from './recipes/EmptyState.recipe';
import { iconRecipe } from './recipes/Icon.recipe';
import { listRecipe } from './recipes/List.recipe';
import { codeRecipe } from './recipes/Code.recipe';
import { codeblockRecipe } from './recipes/Codeblock.recipe';
import { linkRecipe } from './recipes/Link.recipe';
import { separatorRecipe } from './recipes/Separator.recipe';
import { tooltipRecipe } from './recipes/Tooltip.recipe';

export const Colors = {
  Halton: {
    blue: '#2499D5',
    red: '#D73526',
    green: '#009641',
    yellow: '#F39200',
  },
  dark: '#111111',
  grey: {
    light: {
      0: '#F9F9F9',
      1: '#EDEDED',
      2: '#D9D9D9',
      3: '#9D9D9D',
    },
    dark: {
      1: '#5D5D5D',
      2: '#202020',
    },
    blue: {
      1: '#F9FAFC',
    },
    '700': '#414651',
  },
  items: {
    diffuser: '#CD24D6',
    ceiling: '#ffc225',
  },
};

const {
  tokens: defaultTokens,
  animationStyles: defaultAnimationStyles,
  keyframes: defaultKeyframes,
  breakpoints: defaultBreakpoints,
} = defaultConfig.theme ?? {
  tokens: {},
  animationStyles: {},
  keyframes: {},
};

const themeConfig = defineConfig({
  globalCss: {
    '*': {
      boxSizing: 'border-box',
      fontFamily: 'inherit',
    },
    'html, body': {
      width: '100%',
      height: '100%',
      padding: '0px',
      margin: '0px',
      fontFamily: 'Roboto, Arial, sans-serif',
    },
    '@property --pulser-start': {
      // @ts-expect-error Chakra UI types do not include this property
      syntax: '"<angle>"',
      inherits: 'false',
      initialValue: '0deg',
    },
    '@property --pulser-end': {
      // @ts-expect-error Chakra UI types do not include this property
      syntax: '"<angle>"',
      inherits: 'false',
      initialValue: '0deg',
    },
    ':root': {
      '@supports (interpolate-size: allow-keywords)': {
        interpolateSize: 'allow-keywords',
      },
    },
  },
  theme: {
    breakpoints: {
      ...defaultBreakpoints,
    },
    tokens: {
      ...defaultTokens,
      colors: {
        ...defaultTokens?.colors,
        dark: {
          value: '#111111',
        },
        blue: {
          light: {
            value: '#EEF7FD',
          },
        },
        grey: {
          light: {
            0: {
              value: '#F9F9F9',
            },
            1: {
              value: '#EDEDED',
            },
            2: {
              value: '#D9D9D9',
            },
            3: {
              value: '#9D9D9D',
            },
          },
          dark: {
            1: {
              value: '#5D5D5D',
            },
            2: {
              value: '#202020',
            },
          },
          blue: {
            1: {
              value: '#F9FAFC',
            },
          },
          '700': {
            value: '#414651',
          },
        },
        halton: {
          red: {
            value: '#D73526',
          },
          green: {
            value: '#009641',
          },
          yellow: {
            value: '#F39200',
          },
          blue: {
            value: '#2499D5',
          },
        },
        active: {
          value: '#202020',
        },
        bg: {
          DEFAULT: {
            value: '#FFFFFF',
          },
          subtle: {
            value: '#FAFCFF',
          },
          paleblue: {
            value: '#F4F9FD',
          },
          palegrey: {
            value: '#FAFBFC',
          },
          emphasis: {
            value: '#F9F9F9',
          },
          hover: {
            value: '#F0F0F0',
          },
          selected: {
            value: '#202020',
          },
        },
        border: {
          DEFAULT: {
            value: '#CECECE',
          },
          subtle: {
            value: '#D9D9D9',
          },
          dim: {
            value: '#EDEDED',
          },
          active: {
            value: '#202020',
          },
        },
        text: {
          DEFAULT: {
            value: '#111111',
          },
          sub: {
            value: '#414651',
          },
          item: {
            value: '#5D5D5D',
          },
        },
      },
      borders: {
        ...defaultTokens?.borders,
        primary: {
          value: '1px solid {colors.grey.light.3}',
        },
        subtle: {
          value: '1px solid {colors.grey.light.1}',
        },
        dim: {
          value: '1px solid {colors.border.dim}',
        },
        active: {
          value: '1px solid {colors.border.active}',
        },
        reallySubtle: {
          value: '1px solid {colors.bg.emphasis}',
        },
      },
      sizes: {
        ...defaultTokens?.sizes,
        header: {
          height: {
            value: '80px',
          },
        },
        content: {
          height: {
            value: 'calc(100vh - 80px)',
          },
          width: {
            value: '1600px',
          },
        },
      },
      radii: {
        ...defaultTokens?.radii,
        double: {
          value: '20px',
        },
        main: {
          value: '10px',
        },
        smaller: {
          value: '8px',
        },
        small: {
          value: '4px',
        },
      },
      durations: {
        ...defaultTokens?.durations,
        debug: {
          value: '5s',
        },
      },
      gradients: {
        ...defaultTokens?.gradients,
        project: {
          bg: {
            value: 'linear-gradient(0deg, #F6FBFF 38.5%, #FFF 100%);',
          },
        },
      },
      fonts: {
        acMonospace: {
          value: 'menlo, "Cascadia Mono", "Lucida Console", Inconsolata, Consolas, monospace',
        },
        workSans: {
          value: '"Work Sans"',
        },
        acBase: {
          value: 'Roboto, Arial, sans-serif',
        },
      },
    },
    recipes: {
      input: inputRecipe,
      textarea: textareaRecipe,
      button: buttonRecipe,
      heading: headingRecipe,
      text: textRecipe,
      panel: panelRecipe,
      badge: badgeRecipe,
      closeButton: closeButtonRecipe,
      icon: iconRecipe,
      code: codeRecipe,
      link: linkRecipe,
      separator: separatorRecipe,
    },
    slotRecipes: {
      select: selectRecipe,
      menu: menuSlotRecipe,
      header: headerRecipe,
      field: fieldRecipe,
      fieldset: fieldsetRecipe,
      radioGroup: radioGroupRecipe,
      numberInput: numberInputRecipe,
      editable: editableRecipe,
      drawer: drawerRecipe,
      switch: switchRecipe,
      dialog: dialogSlotRecipe,
      combobox: comboboxRecipe,
      emptyState: emptyStateRecipe,
      list: listRecipe,
      codeblock: codeblockRecipe,
      tooltip: tooltipRecipe,
    },
    animationStyles: {
      ...defaultAnimationStyles,
      ...animationStyles,
    },
    keyframes: {
      ...defaultKeyframes,
      ...keyframes,
    },
  },
});

export const system = createSystem(defaultBaseConfig, themeConfig);
